import { call, put, takeLatest } from 'redux-saga/effects'
import { navigate } from '@reach/router'

// redux
import * as authenticationModule from '../modules/authentication'
import * as purchaseRequestBatchesModule from '../modules/purchaseRequestBatches'
import * as purchaseRequestsModule from '../modules/purchaseRequests'
import * as sefModule from '../modules/sef'

import * as authApi from '../apiModules/authentication'
import * as purchaseRequestBatchesApi from '../apiModules/purchaseRequestBatches'
import * as purchaseRequestsApi from '../apiModules/purchaseRequests'
import * as sefApi from '../apiModules/sef'

// utils
import { setLocalStorageValues } from '../../utils/localStorageManager'

const cleanPayload = ({ payload }) => {
  const rows = (payload.rows || [])
    .filter(r => r)
    .map(r => {
      const keys = Object.keys(r)
      const newRow = {}

      keys.forEach(key => {
        const val = r[key]
        const isProvided = val !== 'NOT PROVIDED'

        newRow[key] = isProvided ? val : null
      })

      return newRow
    })

  const cleanedPayload = {
    ...payload,
    rows
  }

  return cleanedPayload
}

function* createAgreementTemplate({ payload }) {
  try {
    const formDataVals = new FormData()

    Object.keys(payload).map(key => {
      if (key === 'multi_documents') return

      if (key === 'new_multi_documents') {
        const files = payload[key] || []
        files.forEach(file => {
          formDataVals.append('new_multi_documents', file)
        })
        return
      }

      if (key === 'removed_multi_document_ids') {
        if ((payload[key] || []).length) {
          formDataVals.append('removed_multi_document_ids', JSON.stringify(payload[key]))
        }
        return
      }

      if (key === 'content') {
        if ((payload[key] || []).length) {
          formDataVals.append('content', JSON.stringify(payload[key]))
        }
        return
      }

      if (payload[key]) {
        formDataVals.append(key, payload[key])
      }
    })

    yield call(sefApi.createAgreementTemplate, formDataVals)
    yield put(sefModule.createAgreementTemplateSuccess())

    navigate(`/dashboard/agreements`)
  } catch (err) {
    yield put(sefModule.createAgreementTemplateFailure(err.response.data))
  }
}

function* createBudget({ payload }) {
  try {
    yield call(sefApi.createBudget, payload)
    yield put(sefModule.createBudgetSuccess())

    navigate(`/dashboard/budgets`)
  } catch (err) {
    yield put(sefModule.createBudgetFailure(err.response.data))
  }
}

function* createBudgetCategory({ payload }) {
  try {
    yield call(sefApi.createBudgetCategory, payload)
    yield put(sefModule.createBudgetCategorySuccess())

    navigate(`/dashboard/budget-categories`)
  } catch (err) {
    yield put(sefModule.createBudgetCategoryFailure(err.response.data))
  }
}

function* createOutputArea({ payload }) {
  try {
    yield call(sefApi.createOutputArea, payload)
    yield put(sefModule.createOutputAreaSuccess())

    navigate(`/dashboard/output-areas`)
  } catch (err) {
    yield put(sefModule.createOutputAreaFailure(err.response.data))
  }
}

function* createOutputIndicator({ payload }) {
  try {
    yield call(sefApi.createOutputIndicator, payload)
    yield put(sefModule.createOutputIndicatorSuccess())

    navigate(`/dashboard/output-indicators`)
  } catch (err) {
    yield put(sefModule.createOutputIndicatorFailure(err.response.data))
  }
}

function* createPartner({ payload }) {
  try {
    yield call(sefApi.createPartner, payload)
    yield put(sefModule.createPartnerSuccess())

    navigate(`/dashboard/partners`)
  } catch (err) {
    yield put(sefModule.createPartnerFailure(err.response.data))
  }
}

function* createPayfile({ payload }) {
  try {
    yield call(sefApi.createPayfile, payload)
    yield put(sefModule.createPayfileSuccess())

    navigate(`/dashboard/payroll`)
  } catch (err) {
    yield put(sefModule.createPayfileFailure(err.response.data))
  }
}

function* createPartnerDistrict({ payload }) {
  try {
    yield call(sefApi.createPartnerDistrict, payload)
    yield put(sefModule.createPartnerDistrictSuccess())

    navigate(`/dashboard/districts`)
  } catch (err) {
    yield put(sefModule.createPartnerDistrictFailure(err.response.data))
  }
}

function* createPurchaseRequest({ payload }) {
  try {
    const formDataVals = new FormData()

    Object.keys(payload).map(value => {
      const isLineItemValue = ['lineItems'].includes(value)

      if (payload[value] && isLineItemValue) {
        formDataVals.append(value, JSON.stringify(payload[value]))
      } else if (payload[value]) {
        formDataVals.append(value, payload[value])
      }
    })

    const { data } = yield call(purchaseRequestsApi.createPurchaseRequest, formDataVals)
    yield put(purchaseRequestsModule.createPurchaseRequestSuccess({ data }))

    navigate(`/dashboard/purchase-requests`)
  } catch (err) {
    const errorMessage = err.response.data
    yield put(purchaseRequestsModule.createPurchaseRequestFailure({ errorMessage, data: payload }))
  }
}

function* createPurchaseRequestBatch({ payload }) {
  try {
    const formDataVals = new FormData()

    Object.keys(payload).map(value => {
      if (payload[value]) {
        formDataVals.append(value, payload[value])
      }
    })

    const { data } = yield call(purchaseRequestBatchesApi.createPurchaseRequestBatch, formDataVals)
    yield put(purchaseRequestBatchesModule.createPurchaseRequestBatchSuccess({ data }))

    navigate(`/dashboard/purchase-requests/batches`)
  } catch (err) {
    const errorMessage = err.response.data
    yield put(
      purchaseRequestBatchesModule.createPurchaseRequestBatchFailure({
        errorMessage,
        data: payload
      })
    )
  }
}

function* createSupplier({ payload }) {
  try {
    const formDataVals = new FormData()

    Object.keys(payload).map(value => {
      if (payload[value]) {
        formDataVals.append(value, payload[value])
      }
    })

    yield call(sefApi.createSupplier, formDataVals)
    yield put(sefModule.createSupplierSuccess())

    navigate(`/dashboard/suppliers`)
  } catch (err) {
    yield put(sefModule.createSupplierFailure(err.response.data))
  }
}

function* createWorksite({ payload }) {
  try {
    yield call(sefApi.createWorksite, payload)
    yield put(sefModule.createWorksiteSuccess())

    navigate(`/dashboard/worksites`)
  } catch (err) {
    yield put(sefModule.createWorksiteFailure(err.response.data))
  }
}

function* createWorkspace({ payload }) {
  try {
    yield call(sefApi.createWorkspace, payload)
    yield put(sefModule.createWorkspaceSuccess())

    navigate(`/dashboard/workspaces`)
  } catch (err) {
    yield put(sefModule.createWorkspaceFailure(err.response.data))
  }
}

function* createWorkspaceProject({ payload }) {
  try {
    yield call(sefApi.createWorkspaceProject, payload)
    yield put(sefModule.createWorkspaceProjectSuccess())

    navigate(`/dashboard/workspace-projects`)
  } catch (err) {
    yield put(sefModule.createWorkspaceProjectFailure(err.response.data))
  }
}

function* importOutputs({ payload }) {
  try {
    const cleanedPayload = cleanPayload({ payload })
    const { data } = yield call(sefApi.importOutputs, cleanedPayload)
    yield put(sefModule.importOutputsSuccess({ data }))

    navigate(`/dashboard/outputs`)
  } catch (err) {
    const errorMessage = err.response.data
    yield put(sefModule.importOutputsFailure(errorMessage))
  }
}

function* importParticipants({ payload }) {
  try {
    const cleanedPayload = cleanPayload({ payload })
    const { data } = yield call(sefApi.importParticipants, cleanedPayload)
    yield put(sefModule.importParticipantsSuccess({ data }))

    navigate(`/dashboard/participants?importedParticipants=success`)
  } catch (err) {
    const errorMessage = err.response.data
    yield put(sefModule.importParticipantsFailure({ errorMessage, data: payload }))
  }
}

function* importKwantuData({ payload }) {
  try {
    const cleanedPayload = cleanPayload({ payload })

    const { data } = yield call(sefApi.importKwantuData, cleanedPayload)
    yield put(sefModule.importKwantuDataSuccess({ data }))

    navigate(`/dashboard/bulk-actions?kwantuImportSuccess=success`)
  } catch (err) {
    const errorMessage = err.response.data
    yield put(sefModule.importKwantuDataFailure({ errorMessage, data: payload }))
  }
}

function* prepareKwantuData({ payload }) {
  try {
    const cleanedPayload = cleanPayload({ payload })
    const formDataVals = new FormData()

    Object.keys(cleanedPayload).map(value => {
      if (cleanedPayload[value]) {
        formDataVals.append(value, cleanedPayload[value])
      }
    })

    const { data } = yield call(sefApi.prepareKwantuData, formDataVals)
    yield put(sefModule.prepareKwantuDataSuccess({ data }))
  } catch (err) {
    const errorMessage = err.response.data
    yield put(sefModule.prepareKwantuDataFailure({ errorMessage, data: payload }))
  }
}

function* promoteParticipants({ payload }) {
  try {
    const cleanedPayload = cleanPayload({ payload })
    const { data } = yield call(sefApi.promoteParticipants, cleanedPayload)
    yield put(sefModule.promoteParticipantsSuccess({ data }))

    navigate(`/dashboard/participants`)
  } catch (err) {
    const errorMessage = err.response.data
    yield put(sefModule.promoteParticipantsFailure({ errorMessage, data: payload }))
  }
}

function* generatePayfile({ payload }) {
  try {
    const response = yield call(sefApi.generatePayfile, payload)
    yield put(sefModule.generatePayfileSuccess(response))
    navigate(`/dashboard/generate-payfile/?generateSuccess=true`)
  } catch (err) {
    yield put(sefModule.generatePayfileFailure(err.response.data))
  }
}

function* initiatePayrollRequest({ payload }) {
  try {
    const response = yield call(sefApi.initiatePayrollRequest, payload)
    yield put(sefModule.initiatePayrollRequestSuccess(response))

    navigate(`/dashboard/payroll-requests/${response.uuid}?justRequested=true`)
  } catch (err) {
    yield put(sefModule.initiatePayrollRequestFailure(err.response.data))
  }
}

function* initiatePurchaseRequestPayment({ payload }) {
  try {
    const response = yield call(purchaseRequestsApi.initiatePurchaseRequestPayment, payload)
    yield put(purchaseRequestsModule.initiatePurchaseRequestPaymentSuccess(response))

    navigate(`/dashboard/purchase-requests/${payload.purchaseRequestId}?justRequested=true`)
  } catch (err) {
    yield put(purchaseRequestsModule.initiatePurchaseRequestPaymentFailure(err.response.data))
  }
}

function* deleteBudgetCategory({ payload }) {
  try {
    const { budgetCategoryId } = payload
    yield call(sefApi.deleteBudgetCategory, { budgetCategoryId })
    yield put(sefModule.deleteBudgetCategorySuccess(payload))

    navigate(`/dashboard/budget-categories`)
  } catch (err) {
    yield put(sefModule.deleteBudgetCategoryFailure(err))
  }
}

function* deleteOutput({ payload }) {
  try {
    const { outputId } = payload
    yield call(sefApi.deleteOutput, { outputId })
    yield put(sefModule.deleteOutputSuccess(payload))

    navigate(`/dashboard/outputs`)
  } catch (err) {
    yield put(sefModule.deleteOutputFailure(err))
  }
}

function* deleteOutputArea({ payload }) {
  try {
    const { outputAreaId } = payload
    yield call(sefApi.deleteOutputArea, { outputAreaId })
    yield put(sefModule.deleteOutputAreaSuccess(payload))

    navigate(`/dashboard/output-areas`)
  } catch (err) {
    yield put(sefModule.deleteOutputAreaFailure(err))
  }
}

function* deleteOutputIndicator({ payload }) {
  try {
    const { indicatorId } = payload
    yield call(sefApi.deleteOutputIndicator, { indicatorId })
    yield put(sefModule.deleteOutputIndicatorSuccess(payload))

    navigate(`/dashboard/output-indicators`)
  } catch (err) {
    yield put(sefModule.deleteOutputIndicatorFailure(err))
  }
}

function* deletePartnerDistrict({ payload }) {
  try {
    const { partnerDistrictId } = payload
    yield call(sefApi.deletePartnerDistrict, { partnerDistrictId })
    yield put(sefModule.deletePartnerDistrictSuccess(payload))

    navigate(`/dashboard/districts`)
  } catch (err) {
    yield put(sefModule.deletePartnerDistrictFailure(err))
  }
}

function* deletePurchaseRequest({ payload }) {
  try {
    const { purchaseRequestId } = payload
    yield call(purchaseRequestsApi.deletePurchaseRequest, { purchaseRequestId })
    yield put(purchaseRequestsModule.deletePurchaseRequestSuccess(payload))

    navigate(`/dashboard/purchase-requests`)
  } catch (err) {
    yield put(purchaseRequestsModule.deletePurchaseRequestFailure(err))
  }
}

function* deletePurchaseRequestBatch({ payload }) {
  try {
    const { purchaseRequestId } = payload
    yield call(purchaseRequestBatchesApi.deletePurchaseRequestBatch, { purchaseRequestId })
    yield put(purchaseRequestBatchesModule.deletePurchaseRequestBatchSuccess(payload))

    navigate(`/dashboard/purchase-requests`)
  } catch (err) {
    yield put(purchaseRequestBatchesModule.deletePurchaseRequestBatchFailure(err))
  }
}

function* deleteSupplier({ payload }) {
  try {
    const { supplierId } = payload
    yield call(sefApi.deleteSupplier, { supplierId })
    yield put(sefModule.deleteSupplierSuccess(payload))

    navigate(`/dashboard/suppliers`)
  } catch (err) {
    yield put(sefModule.deleteSupplierFailure(err))
  }
}

function* deleteWorksite({ payload }) {
  try {
    const { worksiteId } = payload
    yield call(sefApi.deleteWorksite, { worksiteId })
    yield put(sefModule.deleteWorksiteSuccess(payload))

    navigate(`/dashboard/worksites`)
  } catch (err) {
    yield put(sefModule.deleteWorksiteFailure(err))
  }
}

function* deleteWorkspace({ payload }) {
  try {
    const { workspaceId } = payload
    yield call(sefApi.deleteWorkspace, { workspaceId })
    yield put(sefModule.deleteWorkspaceSuccess(payload))

    navigate(`/dashboard/workspaces`)
  } catch (err) {
    yield put(sefModule.deleteWorkspaceFailure(err))
  }
}

function* deleteWorkspaceProject({ payload }) {
  try {
    const { workspaceProjectId } = payload
    yield call(sefApi.deleteWorkspaceProject, { workspaceProjectId })
    yield put(sefModule.deleteWorkspaceProjectSuccess(payload))

    navigate(`/dashboard/workspace-projects`)
  } catch (err) {
    yield put(sefModule.deleteWorkspaceProjectFailure(err))
  }
}

function* fetchActivityReports({ payload }) {
  try {
    const response = yield call(sefApi.fetchActivityReports, payload)
    yield put(sefModule.fetchActivityReportsSuccess(response))
  } catch (err) {
    yield put(sefModule.fetchActivityReportsFailure(err))
  }
}

function* fetchAdmins({ payload }) {
  try {
    const response = yield call(sefApi.fetchAdmins, payload)
    yield put(sefModule.fetchAdminsSuccess(response))
  } catch (err) {
    yield put(sefModule.fetchAdminsFailure(err))
  }
}

function* fetchAgreementTemplate({ payload }) {
  try {
    const response = yield call(sefApi.fetchAgreementTemplate, payload)
    yield put(sefModule.fetchAgreementTemplateSuccess(response))
  } catch (err) {
    yield put(sefModule.fetchAgreementTemplateFailure(err))
  }
}

function* fetchAgreementTemplates({ payload }) {
  try {
    const response = yield call(sefApi.fetchAgreementTemplates, payload)
    yield put(sefModule.fetchAgreementTemplatesSuccess(response))
  } catch (err) {
    yield put(sefModule.fetchAgreementTemplatesFailure(err))
  }
}

function* fetchBudget({ payload }) {
  try {
    const response = yield call(sefApi.fetchBudget, payload)
    yield put(sefModule.fetchBudgetSuccess(response))
  } catch (err) {
    yield put(sefModule.fetchBudgetFailure(err))
  }
}

function* fetchBudgets({ payload }) {
  try {
    const response = yield call(sefApi.fetchBudgets, payload)
    yield put(sefModule.fetchBudgetsSuccess(response))
  } catch (err) {
    yield put(sefModule.fetchBudgetsFailure(err))
  }
}

function* fetchBudgetCategories({ payload }) {
  try {
    const response = yield call(sefApi.fetchBudgetCategories, payload)
    yield put(sefModule.fetchBudgetCategoriesSuccess(response))
  } catch (err) {
    yield put(sefModule.fetchBudgetCategoriesFailure(err))
  }
}

function* fetchBudgetCategory({ payload }) {
  try {
    const response = yield call(sefApi.fetchBudgetCategory, payload)
    yield put(sefModule.fetchBudgetCategorySuccess(response))
  } catch (err) {
    yield put(sefModule.fetchBudgetCategoryFailure(err))
  }
}

function* fetchDistricts({ payload }) {
  try {
    const { employeeInviteToken } = payload
    if (employeeInviteToken) {
      const response = yield call(sefApi.fetchDistrictsForOnboarding, payload)
      yield put(sefModule.fetchDistrictsSuccess(response))
    }

    const response = yield call(sefApi.fetchDistricts, payload)
    yield put(sefModule.fetchDistrictsSuccess(response))
  } catch (err) {
    yield put(sefModule.fetchDistrictsFailure(err))
  }
}

function* fetchEmployeeDetails({ payload }) {
  try {
    const response = yield call(sefApi.fetchEmployeeDetails, payload)
    yield put(sefModule.fetchEmployeeDetailsSuccess(response))
  } catch (err) {
    yield put(sefModule.fetchEmployeeDetailsFailure(err))
  }
}

function* fetchMunicipalities({ payload }) {
  try {
    const response = yield call(sefApi.fetchMunicipalities, payload)
    yield put(sefModule.fetchMunicipalitiesSuccess(response))
  } catch (err) {
    yield put(sefModule.fetchMunicipalitiesFailure(err))
  }
}

function* fetchOnboardingQuestions({ payload }) {
  try {
    const response = yield call(sefApi.fetchOnboardingQuestions, payload)
    yield put(sefModule.fetchOnboardingQuestionsSuccess(response))
  } catch (err) {
    yield put(sefModule.fetchOnboardingQuestionsFailure(err))
  }
}

function* fetchOutputArea({ payload }) {
  try {
    const response = yield call(sefApi.fetchOutputArea, payload)
    yield put(sefModule.fetchOutputAreaSuccess(response))
  } catch (err) {
    yield put(sefModule.fetchOutputAreaFailure(err))
  }
}

function* fetchOutputAreas({ payload }) {
  try {
    const response = yield call(sefApi.fetchOutputAreas, payload)
    yield put(sefModule.fetchOutputAreasSuccess(response))
  } catch (err) {
    yield put(sefModule.fetchOutputAreasFailure(err))
  }
}

function* fetchOutputIndicator({ payload }) {
  try {
    const response = yield call(sefApi.fetchOutputIndicator, payload)
    yield put(sefModule.fetchOutputIndicatorSuccess(response))
  } catch (err) {
    yield put(sefModule.fetchOutputIndicatorFailure(err))
  }
}

function* fetchOutputIndicators({ payload }) {
  try {
    const response = yield call(sefApi.fetchOutputIndicators, payload)
    yield put(sefModule.fetchOutputIndicatorsSuccess(response))
  } catch (err) {
    yield put(sefModule.fetchOutputIndicatorsFailure(err))
  }
}

function* fetchOutput({ payload }) {
  try {
    const response = yield call(sefApi.fetchOutput, payload)
    yield put(sefModule.fetchOutputSuccess(response))
  } catch (err) {
    yield put(sefModule.fetchOutputFailure(err))
  }
}

function* fetchOutputs({ payload }) {
  try {
    const response = yield call(sefApi.fetchOutputs, payload)
    yield put(sefModule.fetchOutputsSuccess(response))
  } catch (err) {
    yield put(sefModule.fetchOutputsFailure(err))
  }
}

function* fetchOwnProfileDetails({ payload }) {
  try {
    const response = yield call(sefApi.fetchOwnProfileDetails, payload)
    yield put(sefModule.fetchOwnProfileDetailsSuccess(response))
  } catch (err) {
    yield put(sefModule.fetchOwnProfileDetailsFailure(err))
  }
}

function* fetchPartnerDistrict({ payload }) {
  try {
    const response = yield call(sefApi.fetchPartnerDistrict, payload)
    yield put(sefModule.fetchPartnerDistrictSuccess(response))
  } catch (err) {
    yield put(sefModule.fetchPartnerDistrictFailure(err))
  }
}

function* fetchPartnerDistricts({ payload }) {
  try {
    const response = yield call(sefApi.fetchPartnerDistricts, payload)
    yield put(sefModule.fetchPartnerDistrictsSuccess(response))
  } catch (err) {
    yield put(sefModule.fetchPartnerDistrictsFailure(err))
  }
}

function* fetchPartners({ payload }) {
  try {
    const response = yield call(sefApi.fetchPartners, payload)
    yield put(sefModule.fetchPartnersSuccess(response))
  } catch (err) {
    yield put(sefModule.fetchPartnersFailure(err))
  }
}

function* fetchPayrollBalance({ payload }) {
  try {
    const response = yield call(sefApi.fetchPayrollBalance, payload)
    yield put(sefModule.fetchPayrollBalanceSuccess(response))
  } catch (err) {
    yield put(sefModule.fetchPayrollBalanceFailure(err))
  }
}

function* fetchPayrollHistory({ payload }) {
  try {
    const response = yield call(sefApi.fetchPayrollHistory, payload)
    yield put(sefModule.fetchPayrollHistorySuccess(response))
  } catch (err) {
    yield put(sefModule.fetchPayrollHistoryFailure(err.response.data))
  }
}

function* fetchPayrollPaymentRequest({ payload }) {
  try {
    const response = yield call(sefApi.fetchPayrollPaymentRequest, payload)
    yield put(sefModule.fetchPayrollPaymentRequestSuccess(response))
  } catch (err) {
    yield put(sefModule.fetchPayrollPaymentRequestFailure(err.response.data))
  }
}

function* fetchPayrollPaymentRequests({ payload }) {
  try {
    const response = yield call(sefApi.fetchPayrollPaymentRequests, payload)
    yield put(sefModule.fetchPayrollPaymentRequestsSuccess(response))
  } catch (err) {
    yield put(sefModule.fetchPayrollPaymentRequestsFailure(err))
  }
}

function* fetchPlace({ payload }) {
  try {
    const response = yield call(sefApi.fetchPlace, payload)
    yield put(sefModule.fetchPlaceSuccess(response))
  } catch (err) {
    yield put(sefModule.fetchPlaceFailure(err))
  }
}

function* fetchPlaces({ payload }) {
  try {
    const response = yield call(sefApi.fetchPlaces, payload)
    yield put(sefModule.fetchPlacesSuccess(response))
  } catch (err) {
    yield put(sefModule.fetchPlacesFailure(err))
  }
}

function* fetchPurchaseRequest({ payload }) {
  try {
    const response = yield call(purchaseRequestsApi.fetchPurchaseRequest, payload)
    yield put(purchaseRequestsModule.fetchPurchaseRequestSuccess(response))
  } catch (err) {
    yield put(purchaseRequestsModule.fetchPurchaseRequestFailure(err))
  }
}

function* fetchPurchaseRequests({ payload }) {
  try {
    const response = yield call(purchaseRequestsApi.fetchPurchaseRequests, payload)
    yield put(purchaseRequestsModule.fetchPurchaseRequestsSuccess(response))
  } catch (err) {
    yield put(purchaseRequestsModule.fetchPurchaseRequestsFailure(err))
  }
}

function* fetchPurchaseRequestBatch({ payload }) {
  try {
    const response = yield call(purchaseRequestBatchesApi.fetchPurchaseRequestBatch, payload)
    yield put(purchaseRequestBatchesModule.fetchPurchaseRequestBatchSuccess(response))
  } catch (err) {
    yield put(purchaseRequestBatchesModule.fetchPurchaseRequestBatchFailure(err))
  }
}

function* fetchPurchaseRequestBatches({ payload }) {
  try {
    const response = yield call(purchaseRequestBatchesApi.fetchPurchaseRequestBatches, payload)
    yield put(purchaseRequestBatchesModule.fetchPurchaseRequestBatchesSuccess(response))
  } catch (err) {
    yield put(purchaseRequestBatchesModule.fetchPurchaseRequestBatchesFailure(err))
  }
}

function* fetchScaffoldData({ payload }) {
  try {
    const response = yield call(sefApi.fetchScaffoldData, payload)
    yield put(sefModule.fetchScaffoldDataSuccess(response))
  } catch (err) {
    yield put(sefModule.fetchScaffoldDataFailure(err))
  }
}

function* fetchStats({ payload }) {
  try {
    const response = yield call(sefApi.fetchStats, payload)
    yield put(sefModule.fetchStatsSuccess(response))
  } catch (err) {
    yield put(sefModule.fetchStatsFailure(err))
  }
}

function* fetchSupplier({ payload }) {
  try {
    const response = yield call(sefApi.fetchSupplier, payload)
    yield put(sefModule.fetchSupplierSuccess(response))
  } catch (err) {
    yield put(sefModule.fetchSupplierFailure(err))
  }
}

function* fetchSuppliers({ payload }) {
  try {
    const response = yield call(sefApi.fetchSuppliers, payload)
    yield put(sefModule.fetchSuppliersSuccess(response))
  } catch (err) {
    yield put(sefModule.fetchSuppliersFailure(err))
  }
}

function* fetchTimekeepers({ payload }) {
  try {
    const response = yield call(sefApi.fetchTimekeepers, payload)
    yield put(sefModule.fetchTimekeepersSuccess(response))
  } catch (err) {
    yield put(sefModule.fetchTimekeepersFailure(err))
  }
}

function* fetchWards({ payload }) {
  try {
    const response = yield call(sefApi.fetchWards, payload)
    yield put(sefModule.fetchWardsSuccess(response))
  } catch (err) {
    yield put(sefModule.fetchWardsFailure(err))
  }
}

function* fetchWorksite({ payload }) {
  try {
    const response = yield call(sefApi.fetchWorksite, payload)
    yield put(sefModule.fetchWorksiteSuccess(response))
  } catch (err) {
    yield put(sefModule.fetchWorksiteFailure(err))
  }
}

function* fetchWorksites({ payload }) {
  try {
    const response = yield call(sefApi.fetchWorksites, payload)
    yield put(sefModule.fetchWorksitesSuccess(response))
  } catch (err) {
    yield put(sefModule.fetchWorksitesFailure(err))
  }
}

function* fetchWorkspace({ payload }) {
  try {
    const response = yield call(sefApi.fetchWorkspace, payload)
    yield put(sefModule.fetchWorkspaceSuccess(response))
  } catch (err) {
    yield put(sefModule.fetchWorkspaceFailure(err))
  }
}

function* fetchWorkspaces({ payload }) {
  try {
    const response = yield call(sefApi.fetchWorkspaces, payload)
    yield put(sefModule.fetchWorkspacesSuccess(response))
  } catch (err) {
    yield put(sefModule.fetchWorkspacesFailure(err))
  }
}

function* fetchPrograms({ payload }) {
  try {
    const response = yield call(sefApi.fetchPrograms, payload)
    yield put(sefModule.fetchProgramsSuccess(response))
  } catch (err) {
    yield put(sefModule.fetchProgramsFailure(err))
  }
}

function* fetchWorkspaceProject({ payload }) {
  try {
    const response = yield call(sefApi.fetchWorkspaceProject, payload)
    yield put(sefModule.fetchWorkspaceProjectSuccess(response))
  } catch (err) {
    yield put(sefModule.fetchWorkspaceProjectFailure(err))
  }
}

function* fetchWorkspaceProjects({ payload }) {
  try {
    const response = yield call(sefApi.fetchWorkspaceProjects, payload)
    yield put(sefModule.fetchWorkspaceProjectsSuccess(response))
  } catch (err) {
    yield put(sefModule.fetchWorkspaceProjectsFailure(err))
  }
}

function* provisionIndicatorOutcome({ payload }) {
  try {
    const outcome = yield call(sefApi.provisionIndicatorOutcome, payload)
    yield put(sefModule.provisionIndicatorOutcomeSuccess())

    navigate(`/dashboard/outputs/${outcome.uuid}`)
  } catch (err) {
    yield put(sefModule.provisionIndicatorOutcomeFailure(err.response.data))
  }
}

function* requestAgreementZip({ payload }) {
  yield call(sefApi.requestAgreementZip, payload)
}

function* sendOutcomesTrackingReminder({ payload }) {
  try {
    yield call(sefApi.sendOutcomesTrackingReminder, payload)
    yield put(sefModule.sendOutcomesTrackingReminderSuccess())
  } catch (err) {
    yield put(sefModule.sendOutcomesTrackingReminderFailure(err.response.data))
  }
}

function* submitIndicatorOutcome({ payload }) {
  try {
    const formDataVals = new FormData()

    Object.keys(payload).map(value => {
      if (payload[value]) {
        formDataVals.append(value, payload[value])
      }
    })

    yield call(sefApi.submitIndicatorOutcome, formDataVals)
    yield put(sefModule.submitIndicatorOutcomeSuccess(payload))

    const worksiteParam = `?worksiteId=${payload.worksite_id}`
    const partnerIdParam = payload.sef_partner_org_id
      ? `&partnerOrgId=${payload.sef_partner_org_id}`
      : ''
    const frequencyParam = payload.tracking_frequency
      ? `&trackingFrequency=${payload.tracking_frequency.toLowerCase()}`
      : ''

    navigate(`/dashboard/outputs/new${worksiteParam}${partnerIdParam}${frequencyParam}`)
  } catch (err) {
    yield put(sefModule.submitIndicatorOutcomeFailure(err))
  }
}

function* updateAgreementTemplate({ payload }) {
  try {
    const formDataVals = new FormData()

    Object.keys(payload).map(key => {
      if (key === 'multi_documents') return

      if (key === 'new_multi_documents') {
        const files = payload[key] || []
        files.forEach(file => {
          formDataVals.append('new_multi_documents', file)
        })
        return
      }

      if (key === 'removed_multi_document_ids') {
        if ((payload[key] || []).length) {
          formDataVals.append('removed_multi_document_ids', JSON.stringify(payload[key]))
        }
        return
      }

      if (key === 'content') {
        if ((payload[key] || []).length) {
          formDataVals.append('content', JSON.stringify(payload[key]))
        }
        return
      }

      if (payload[key]) {
        formDataVals.append(key, payload[key])
      }
    })

    const updatedAgreement = yield call(sefApi.updateAgreementTemplate, formDataVals)
    yield put(sefModule.updateAgreementTemplateSuccess(updatedAgreement))

    navigate(`/dashboard/agreements`)
  } catch (err) {
    yield put(sefModule.updateAgreementTemplateFailure(err))
  }
}

function* updateBudget({ payload }) {
  try {
    const updatedOutputArea = yield call(sefApi.updateBudget, payload)
    yield put(sefModule.updateBudgetSuccess(updatedOutputArea))

    navigate(`/dashboard/budgets`)
  } catch (err) {
    yield put(sefModule.updateBudgetFailure(err))
  }
}

function* updateBudgetCategory({ payload }) {
  try {
    const updatedOutputArea = yield call(sefApi.updateBudgetCategory, payload)
    yield put(sefModule.updateBudgetCategorySuccess(updatedOutputArea))

    navigate(`/dashboard/budget-categories`)
  } catch (err) {
    yield put(sefModule.updateBudgetCategoryFailure(err))
  }
}

function* updateOutput({ payload }) {
  try {
    const formDataVals = new FormData()

    Object.keys(payload).map(value => {
      formDataVals.append(value, payload[value])
    })

    const { data } = yield call(sefApi.updateOutput, formDataVals)
    yield put(sefModule.updateOutputSuccess({ data }))

    // reload data
    const response = yield call(sefApi.fetchOutputs, payload)
    yield put(sefModule.fetchOutputsSuccess(response))

    navigate(`/dashboard/outputs`)
  } catch (err) {
    yield put(sefModule.updateOutputFailure(err))
  }
}

function* updateOutputArea({ payload }) {
  try {
    const updatedOutputArea = yield call(sefApi.updateOutputArea, payload)
    yield put(sefModule.updateOutputAreaSuccess(updatedOutputArea))

    navigate(`/dashboard/output-areas`)
  } catch (err) {
    yield put(sefModule.updateOutputAreaFailure(err))
  }
}

function* updateOutputIndicator({ payload }) {
  try {
    const updatedOutputIndicator = yield call(sefApi.updateOutputIndicator, payload)
    yield put(sefModule.updateOutputIndicatorSuccess(updatedOutputIndicator))

    navigate(`/dashboard/output-indicators`)
  } catch (err) {
    yield put(sefModule.updateOutputIndicatorFailure(err))
  }
}

function* updatePartner({ payload }) {
  try {
    const updatedPartner = yield call(sefApi.updatePartner, payload)
    yield put(sefModule.updatePartnerSuccess(updatedPartner))

    navigate(`/dashboard/partners`)
  } catch (err) {
    yield put(sefModule.updatePartnerFailure(err))
  }
}

function* updatePartnerDistrict({ payload }) {
  try {
    const updatedDistrict = yield call(sefApi.updatePartnerDistrict, payload)
    yield put(sefModule.updatePartnerDistrictSuccess(updatedDistrict))

    navigate(`/dashboard/districts`)
  } catch (err) {
    yield put(sefModule.updatePartnerDistrictFailure(err))
  }
}

function* updatePayrollRequest({ payload }) {
  try {
    const response = yield call(sefApi.updatePayrollRequest, payload)
    yield put(sefModule.updatePayrollRequestSuccess(response))

    navigate(`/dashboard/payroll-requests?workspaceId=${payload.workspaceId}`)
  } catch (err) {
    yield put(sefModule.updatePayrollRequestFailure(err))
  }
}

function* updatePlace({ payload }) {
  try {
    const updatedPlace = yield call(sefApi.updatePlace, payload)
    yield put(sefModule.updatePlaceSuccess(updatedPlace))

    navigate(`/dashboard/places/`)
  } catch (err) {
    yield put(sefModule.updatePlaceFailure(err))
  }
}

function* updatePurchaseRequest({ payload }) {
  try {
    const formDataVals = new FormData()

    Object.keys(payload).map(value => {
      const isLineItemValue = ['lineItems'].includes(value)

      if (payload[value] && isLineItemValue) {
        formDataVals.append(value, JSON.stringify(payload[value]))
      } else if (payload[value]) {
        formDataVals.append(value, payload[value])
      }
    })

    const { data } = yield call(purchaseRequestsApi.updatePurchaseRequest, formDataVals)
    yield put(purchaseRequestsModule.updatePurchaseRequestSuccess({ data }))

    // reload data
    const response = yield call(purchaseRequestsApi.fetchPurchaseRequests, payload)
    yield put(purchaseRequestsModule.fetchPurchaseRequestsSuccess(response))

    navigate(`/dashboard/purchase-requests`)
  } catch (err) {
    yield put(purchaseRequestsModule.updatePurchaseRequestFailure(err))
  }
}

function* initiatePurchaseRequestBatchPayment({ payload }) {
  try {
    const { data } = yield call(purchaseRequestBatchesApi.initiatePurchaseRequestPayment, payload)
    yield put(purchaseRequestBatchesModule.initiatePurchaseRequestBatchPaymentSuccess({ data }))

    navigate(`/dashboard/purchase-requests/batches`)
  } catch (err) {
    yield put(purchaseRequestBatchesModule.initiatePurchaseRequestBatchPaymentFailure(err))
  }
}

function* updatePurchaseRequestBatch({ payload }) {
  try {
    const formDataVals = new FormData()

    Object.keys(payload).map(value => {
      if (payload[value]) {
        formDataVals.append(value, payload[value])
      }
    })

    const { data } = yield call(purchaseRequestBatchesApi.updatePurchaseRequestBatch, formDataVals)
    yield put(purchaseRequestBatchesModule.updatePurchaseRequestBatchSuccess({ data }))

    window.location.reload()
  } catch (err) {
    yield put(purchaseRequestBatchesModule.updatePurchaseRequestBatchFailure(err))
  }
}

function* updateSupplier({ payload }) {
  try {
    const formDataVals = new FormData()

    Object.keys(payload).map(value => {
      if (payload[value]) {
        formDataVals.append(value, payload[value])
      }
    })

    const updatedSupplier = yield call(sefApi.updateSupplier, formDataVals)
    yield put(sefModule.updateSupplierSuccess(updatedSupplier))

    navigate(`/dashboard/suppliers/`)
  } catch (err) {
    yield put(sefModule.updateSupplierFailure(err))
  }
}

function* updateWorksite({ payload }) {
  try {
    const updatedWorksite = yield call(sefApi.updateWorksite, payload)
    yield put(sefModule.updateWorksiteSuccess(updatedWorksite))

    navigate(`/dashboard/worksites/`)
  } catch (err) {
    yield put(sefModule.updateWorksiteFailure(err))
  }
}

function* updateWorkspace({ payload }) {
  try {
    const updatedWorkspace = yield call(sefApi.updateWorkspace, payload)
    yield put(sefModule.updateWorkspaceSuccess(updatedWorkspace))

    navigate(`/dashboard/workspaces/`)
  } catch (err) {
    yield put(sefModule.updateWorkspaceFailure(err))
  }
}

function* updateWorkspaceProject({ payload }) {
  try {
    const updatedWorkspaceProject = yield call(sefApi.updateWorkspaceProject, payload)
    yield put(sefModule.updateWorkspaceProjectSuccess(updatedWorkspaceProject))

    navigate(`/dashboard/workspace-projects/`)
  } catch (err) {
    yield put(sefModule.updateWorkspaceProjectFailure(err))
  }
}

function* onboardEmployee({ payload }) {
  try {
    const response = yield call(sefApi.onboardEmployee, payload)
    yield put(sefModule.onboardEmployeeSuccess(response))

    yield setLocalStorageValues(response)
    yield put(authenticationModule.loginSuccess(response.data))

    const authResponse = yield call(authApi.fetchMyRoles)
    yield put(authenticationModule.fetchMyRolesSuccess(authResponse))

    return navigate('dashboard')
  } catch (err) {
    yield put(sefModule.onboardEmployeeFailure(err))
  }
}

function* downloadBulkAgreements({ payload }) {
  try {
    yield call(sefApi.downloadBulkAgreements, payload)
    yield put(sefModule.downloadBulkAgreementsSuccess())

    return navigate('/dashboard/participants?bulkDownloadRequest=success')
  } catch (err) {
    yield put(sefModule.downloadBulkAgreementsFailure(err))
  }
}

function* bulkRegenerateAgreements({ payload }) {
  try {
    yield call(sefApi.bulkRegenerateAgreements, payload)
    yield put(sefModule.bulkRegenerateAgreementsSuccess())

    return navigate('/dashboard/participants?bulkRegenerateAgreementsRequest=success')
  } catch (err) {
    yield put(sefModule.bulkRegenerateAgreementsFailure(err))
  }
}

function* bulkUploadDocuments({ payload }) {
  try {
    const formDataVals = new FormData()
    formDataVals.append('zip_file', payload.zip_file)
    formDataVals.append('document_type', payload.document_type)
    if (payload.tax_year) formDataVals.append('tax_year', payload.tax_year)

    const response = yield call(sefApi.bulkUploadDocuments, formDataVals)
    yield put(sefModule.bulkUploadDocumentsSuccess(response))
  } catch (err) {
    const errorMessage = err.response && err.response.data
    yield put(sefModule.bulkUploadDocumentsFailure({ errorMessage }))
  }
}

function* fetchBulkDocumentUploadStatus({ payload }) {
  try {
    const response = yield call(sefApi.fetchBulkDocumentUploadStatus, payload)
    yield put(sefModule.fetchBulkDocumentUploadStatusSuccess(response))
  } catch (err) {
    yield put(sefModule.fetchBulkDocumentUploadStatusFailure(err))
  }
}

function* bulkRegenerateArchives({ payload }) {
  try {
    yield call(sefApi.bulkRegenerateArchives, payload)
    yield put(sefModule.bulkRegenerateArchivesSuccess())

    return navigate('/dashboard/participants?bulkRegenerateArchivesRequest=success')
  } catch (err) {
    yield put(sefModule.bulkRegenerateArchivesFailure(err))
  }
}

function* bulkRegeneratePayslips({ payload }) {
  try {
    yield call(sefApi.bulkRegeneratePayslips, payload)
    yield put(sefModule.bulkRegeneratePayslipsSuccess())

    return navigate('/dashboard/participants?bulkRegeneratePayslipsRequest=success')
  } catch (err) {
    yield put(sefModule.bulkRegeneratePayslipsFailure(err))
  }
}

function* fetchParticipantProfile({ payload }) {
  try {
    const response = yield call(sefApi.fetchParticipantProfile, payload)
    yield put(sefModule.fetchParticipantProfileSuccess(response))
  } catch (err) {
    yield put(sefModule.fetchParticipantProfileFailure(err))
  }
}

export const sefSagas = [
  // Purchase Requests
  takeLatest(purchaseRequestsModule.CREATE_PURCHASE_REQUEST, createPurchaseRequest),
  takeLatest(purchaseRequestsModule.DELETE_PURCHASE_REQUEST, deletePurchaseRequest),
  takeLatest(purchaseRequestsModule.FETCH_PURCHASE_REQUEST, fetchPurchaseRequest),
  takeLatest(purchaseRequestsModule.FETCH_PURCHASE_REQUESTS, fetchPurchaseRequests),
  takeLatest(
    purchaseRequestsModule.INITIATE_PURCHASE_REQUEST_PAYMENT,
    initiatePurchaseRequestPayment
  ),
  takeLatest(purchaseRequestsModule.UPDATE_PURCHASE_REQUEST, updatePurchaseRequest),

  // Purchase Request Batchs
  takeLatest(
    purchaseRequestBatchesModule.CREATE_PURCHASE_REQUEST_BATCH,
    createPurchaseRequestBatch
  ),
  takeLatest(
    purchaseRequestBatchesModule.DELETE_PURCHASE_REQUEST_BATCH,
    deletePurchaseRequestBatch
  ),
  takeLatest(purchaseRequestBatchesModule.FETCH_PURCHASE_REQUEST_BATCH, fetchPurchaseRequestBatch),
  takeLatest(
    purchaseRequestBatchesModule.FETCH_PURCHASE_REQUEST_BATCHES,
    fetchPurchaseRequestBatches
  ),
  takeLatest(
    purchaseRequestBatchesModule.INITIATE_PURCHASE_REQUEST_BATCH_PAYMENT,
    initiatePurchaseRequestBatchPayment
  ),
  takeLatest(
    purchaseRequestBatchesModule.UPDATE_PURCHASE_REQUEST_BATCH,
    updatePurchaseRequestBatch
  ),

  // Rest
  takeLatest(sefModule.CREATE_AGREEMENT_TEMPLATE, createAgreementTemplate),
  takeLatest(sefModule.CREATE_BUDGET, createBudget),
  takeLatest(sefModule.CREATE_BUDGET_CATEGORY, createBudgetCategory),
  takeLatest(sefModule.CREATE_OUTPUT_AREA, createOutputArea),
  takeLatest(sefModule.CREATE_OUTPUT_INDICATOR, createOutputIndicator),
  takeLatest(sefModule.CREATE_PARTNER, createPartner),
  takeLatest(sefModule.CREATE_PAYFILE, createPayfile),
  takeLatest(sefModule.CREATE_PARTNER_DISTRICT, createPartnerDistrict),
  takeLatest(sefModule.CREATE_SUPPLIER, createSupplier),
  takeLatest(sefModule.CREATE_WORKSITE, createWorksite),
  takeLatest(sefModule.CREATE_WORKSPACE, createWorkspace),
  takeLatest(sefModule.CREATE_WORKSPACE_PROJECT, createWorkspaceProject),
  takeLatest(sefModule.DELETE_BUDGET_CATEGORY, deleteBudgetCategory),
  takeLatest(sefModule.DELETE_OUTPUT, deleteOutput),
  takeLatest(sefModule.DELETE_OUTPUT_AREA, deleteOutputArea),
  takeLatest(sefModule.DELETE_OUTPUT_INDICATOR, deleteOutputIndicator),
  takeLatest(sefModule.DELETE_PARTNER_DISTRICT, deletePartnerDistrict),
  takeLatest(sefModule.DELETE_SUPPLIER, deleteSupplier),
  takeLatest(sefModule.DELETE_WORKSITE, deleteWorksite),
  takeLatest(sefModule.DELETE_WORKSPACE, deleteWorkspace),
  takeLatest(sefModule.DELETE_WORKSPACE_PROJECT, deleteWorkspaceProject),
  takeLatest(sefModule.FETCH_ACTIVITY_REPORTS, fetchActivityReports),
  takeLatest(sefModule.FETCH_ADMINS, fetchAdmins),
  takeLatest(sefModule.FETCH_AGREEMENT_TEMPLATE, fetchAgreementTemplate),
  takeLatest(sefModule.FETCH_AGREEMENT_TEMPLATES, fetchAgreementTemplates),
  takeLatest(sefModule.FETCH_BUDGET, fetchBudget),
  takeLatest(sefModule.FETCH_BUDGETS, fetchBudgets),
  takeLatest(sefModule.FETCH_BUDGET_CATEGORY, fetchBudgetCategory),
  takeLatest(sefModule.FETCH_BUDGET_CATEGORIES, fetchBudgetCategories),
  takeLatest(sefModule.FETCH_DISTRICTS, fetchDistricts),
  takeLatest(sefModule.FETCH_EMPLOYEE_DETAILS, fetchEmployeeDetails),
  takeLatest(sefModule.FETCH_MUNICIPALITIES, fetchMunicipalities),
  takeLatest(sefModule.FETCH_ONBOARDING_QUESTIONS, fetchOnboardingQuestions),
  takeLatest(sefModule.FETCH_OUTPUT_AREAS, fetchOutputAreas),
  takeLatest(sefModule.FETCH_OUTPUT_AREA, fetchOutputArea),
  takeLatest(sefModule.FETCH_OUTPUT_INDICATORS, fetchOutputIndicators),
  takeLatest(sefModule.FETCH_OUTPUT_INDICATOR, fetchOutputIndicator),
  takeLatest(sefModule.FETCH_OUTPUT, fetchOutput),
  takeLatest(sefModule.FETCH_OUTPUTS, fetchOutputs),
  takeLatest(sefModule.FETCH_OWN_PROFILE_DETAILS, fetchOwnProfileDetails),
  takeLatest(sefModule.FETCH_PARTNER_DISTRICT, fetchPartnerDistrict),
  takeLatest(sefModule.FETCH_PARTNER_DISTRICTS, fetchPartnerDistricts),
  takeLatest(sefModule.FETCH_PARTNERS, fetchPartners),
  takeLatest(sefModule.FETCH_PAYROLL_BALANCE, fetchPayrollBalance),
  takeLatest(sefModule.FETCH_PAYROLL_HISTORY, fetchPayrollHistory),
  takeLatest(sefModule.FETCH_PAYROLL_PAYMENT_REQUEST, fetchPayrollPaymentRequest),
  takeLatest(sefModule.FETCH_PAYROLL_PAYMENT_REQUESTS, fetchPayrollPaymentRequests),
  takeLatest(sefModule.FETCH_PLACE, fetchPlace),
  takeLatest(sefModule.FETCH_PLACES, fetchPlaces),
  takeLatest(sefModule.FETCH_SCAFFOLD_DATA, fetchScaffoldData),
  takeLatest(sefModule.FETCH_STATS, fetchStats),
  takeLatest(sefModule.FETCH_SUPPLIER, fetchSupplier),
  takeLatest(sefModule.FETCH_SUPPLIERS, fetchSuppliers),
  takeLatest(sefModule.FETCH_TIMEKEEPERS, fetchTimekeepers),
  takeLatest(sefModule.FETCH_WARDS, fetchWards),
  takeLatest(sefModule.FETCH_WORKSITE, fetchWorksite),
  takeLatest(sefModule.FETCH_WORKSITES, fetchWorksites),
  takeLatest(sefModule.FETCH_WORKSPACE, fetchWorkspace),
  takeLatest(sefModule.FETCH_WORKSPACES, fetchWorkspaces),
  takeLatest(sefModule.FETCH_PROGRAMS, fetchPrograms),
  takeLatest(sefModule.FETCH_WORKSPACE_PROJECT, fetchWorkspaceProject),
  takeLatest(sefModule.FETCH_WORKSPACE_PROJECTS, fetchWorkspaceProjects),
  takeLatest(sefModule.IMPORT_OUTPUTS, importOutputs),
  takeLatest(sefModule.IMPORT_KWANTU_DATA, importKwantuData),
  takeLatest(sefModule.PREPARE_KWANTU_DATA, prepareKwantuData),
  takeLatest(sefModule.IMPORT_PARTICIPANTS, importParticipants),
  takeLatest(sefModule.PROMOTE_PARTICIPANTS, promoteParticipants),
  takeLatest(sefModule.GENERATE_PAYFILE, generatePayfile),
  takeLatest(sefModule.INITIATE_PAYROLL_REQUEST, initiatePayrollRequest),
  takeLatest(sefModule.REQUEST_AGREEMENT_ZIP, requestAgreementZip),
  takeLatest(sefModule.SEND_OUTCOMES_TRACKING_REMINDER, sendOutcomesTrackingReminder),
  takeLatest(sefModule.PROVISION_INDICATOR_OUTCOME, provisionIndicatorOutcome),
  takeLatest(sefModule.SUBMIT_INDICATOR_OUTCOME, submitIndicatorOutcome),
  takeLatest(sefModule.UPDATE_AGREEMENT_TEMPLATE, updateAgreementTemplate),
  takeLatest(sefModule.UPDATE_BUDGET, updateBudget),
  takeLatest(sefModule.UPDATE_BUDGET_CATEGORY, updateBudgetCategory),
  takeLatest(sefModule.UPDATE_OUTPUT, updateOutput),
  takeLatest(sefModule.UPDATE_OUTPUT_AREA, updateOutputArea),
  takeLatest(sefModule.UPDATE_OUTPUT_INDICATOR, updateOutputIndicator),
  takeLatest(sefModule.UPDATE_PARTNER, updatePartner),
  takeLatest(sefModule.UPDATE_PARTNER_DISTRICT, updatePartnerDistrict),
  takeLatest(sefModule.UPDATE_PLACE, updatePlace),
  takeLatest(sefModule.UPDATE_PAYROLL_REQUEST, updatePayrollRequest),
  takeLatest(sefModule.UPDATE_SUPPLIER, updateSupplier),
  takeLatest(sefModule.UPDATE_WORKSITE, updateWorksite),
  takeLatest(sefModule.UPDATE_WORKSPACE, updateWorkspace),
  takeLatest(sefModule.UPDATE_WORKSPACE_PROJECT, updateWorkspaceProject),
  takeLatest(sefModule.ONBOARD_EMPLOYEE, onboardEmployee),
  takeLatest(sefModule.DOWNLOAD_BULK_AGREEMENTS, downloadBulkAgreements),
  takeLatest(sefModule.BULK_REGENERATE_AGREEMENTS, bulkRegenerateAgreements),
  takeLatest(sefModule.FETCH_PARTICIPANT_PROFILE, fetchParticipantProfile),
  takeLatest(sefModule.BULK_REGENERATE_ARCHIVES, bulkRegenerateArchives),
  takeLatest(sefModule.BULK_UPLOAD_DOCUMENTS, bulkUploadDocuments),
  takeLatest(sefModule.FETCH_BULK_DOCUMENT_UPLOAD_STATUS, fetchBulkDocumentUploadStatus),
  takeLatest(sefModule.BULK_REGENERATE_PAYSLIPS, bulkRegeneratePayslips)
]
